import { useState } from "react";
import { ArrowLeft, Download, Loader2, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function ArchiveExport() {
  const [, navigate] = useLocation();
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [isExporting, setIsExporting] = useState(false);

  const { data: wells } = trpc.wells.list.useQuery({ activeOnly: true });
  const archiveExportMutation = trpc.system.archiveExport.useMutation();

  // Generate year options (current year and past 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const handleExport = async () => {
    if (!selectedWellId) {
      toast.error("Please select a well");
      return;
    }

    setIsExporting(true);

    try {
      // Get metadata first
      const metadata = await archiveExportMutation.mutateAsync({
        wellId: selectedWellId,
        year: selectedYear,
        month: selectedMonth,
      });

      // Download archive via Express endpoint
      const url = `/api/archive/download?wellId=${selectedWellId}&year=${selectedYear}&month=${selectedMonth}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `archive_${metadata.wellName}_${metadata.period}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Archive exported: ${metadata.totalLogs} logs, ${metadata.totalPhotos} photos`);
    } catch (error: any) {
      toast.error(error.message || "Failed to export archive");
    } finally {
      setIsExporting(false);
    }
  };

  const selectedWell = wells?.find(w => w.id === selectedWellId);

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        <h1 className="text-3xl font-bold">Archive Export</h1>
        <p className="text-muted-foreground mt-2">
          Export monthly archives containing PDF reports, CSV logs, and organized photos
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive className="w-5 h-5" />
            Monthly Archive Package
          </CardTitle>
          <CardDescription>
            Select a well and month to generate a comprehensive archive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Well Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Well</label>
            <Select
              value={selectedWellId?.toString() || ""}
              onValueChange={(value) => setSelectedWellId(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a well" />
              </SelectTrigger>
              <SelectContent>
                {wells?.map((well) => (
                  <SelectItem key={well.id} value={well.id.toString()}>
                    {well.wellName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Year</label>
            <Select
              value={selectedYear.toString()}
              onValueChange={(value) => setSelectedYear(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Month Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Month</label>
            <Select
              value={selectedMonth.toString()}
              onValueChange={(value) => setSelectedMonth(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem key={month.value} value={month.value.toString()}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Preview */}
          {selectedWell && (
            <Alert>
              <AlertDescription>
                <strong>Archive will include:</strong>
                <ul className="mt-2 space-y-1 text-sm">
                  <li>• Monthly report (TXT format)</li>
                  <li>• All daily logs for {months.find(m => m.value === selectedMonth)?.label} {selectedYear} (CSV)</li>
                  <li>• Photos organized by date and instrument</li>
                  <li>• Filenames: {selectedWell.wellName.replace(/[^a-zA-Z0-9]/g, '_')}_{selectedYear}-{String(selectedMonth).padStart(2, '0')}.zip</li>
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Export Button */}
          <Button
            onClick={handleExport}
            disabled={!selectedWellId || isExporting}
            className="w-full"
            size="lg"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating Archive...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download Archive
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
